import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import emailTemplatesApi, { EmailTemplate } from '../services/email-templates-api';
import EmailTemplateManagement from '../components/Email/EmailTemplateManagement'; // 09/02/2026 - Added for email template management
import EmailTemplateBuilder from '../components/Email/EmailTemplateBuilder'; // 09/02/2026 - Added for email template builder

const EmailTemplatesPage: React.FC = () => {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showBuilder, setShowBuilder] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<EmailTemplate | null>(null);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const data = await emailTemplatesApi.list(false); // Include inactive
      setTemplates(data);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  const handleCreate = () => {
    setEditingTemplate(null);
    setShowBuilder(true);
  };

  const handleEdit = (template: EmailTemplate) => {
    setEditingTemplate(template);
    setShowBuilder(true);
  };

  const handleDelete = async (templateId: number) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    try {
      await emailTemplatesApi.delete(templateId);
      fetchTemplates();
    } catch (err: any) {
      setError(err.message || 'Failed to delete template');
    }
  };

  const handleSave = async () => {
    setShowBuilder(false);
    setEditingTemplate(null);
    fetchTemplates();
  };

  const handleCancel = () => {
    setShowBuilder(false);
    setEditingTemplate(null);
  };

  if (showBuilder) {
    return (
      <EmailTemplateBuilder
        template={editingTemplate}
        onSave={handleSave}
        onCancel={handleCancel}
      />
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Email Templates</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreate}
        >
          Create Template
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <EmailTemplateManagement
          templates={templates}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onRefresh={fetchTemplates}
        />
      )}
    </Box>
  );
};

export default EmailTemplatesPage;
